import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type InputWithIconType = 'text' | 'email' | 'password' | 'tel' | 'number';

@Component({
  selector: 'or-input-with-icon',
  templateUrl: './input-with-icon.component.html',
  styleUrls: ['./input-with-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputWithIconComponent),
      multi: true
    }
  ]
})
export class InputWithIconComponent implements ControlValueAccessor {
  readonly placeholder: InputSignal<string> = input<string>('');
  readonly type: InputSignal<InputWithIconType> = input<InputWithIconType>('text');
  readonly autocomplete: InputSignal<string | undefined> = input<string | undefined>();
  readonly icon: InputSignal<string | undefined> = input<string | undefined>();

  readonly iconClick: OutputEmitterRef<void> = output<void>();

  readonly value: WritableSignal<string> = signal<string>('');
  readonly disabled: WritableSignal<boolean> = signal<boolean>(false);
  readonly focused: WritableSignal<boolean> = signal<boolean>(false);

  private onChange: (value: string) => void = (): void => undefined;
  private onTouched: () => void = (): void => undefined;

  writeValue(value: string): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onInput(event: Event): void {
    const inputValue: string = (event.target as HTMLInputElement).value;
    this.value.set(inputValue);
    this.onChange(inputValue);
  }

  onBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  onFocus(): void {
    this.focused.set(true);
  }

  onIconClick(): void {
    this.iconClick.emit();
  }
}
